import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Star, ShieldCheck, Sparkles, Clock, MapPin, HelpingHand, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import PhotoUpload from "../components/PhotoUpload";
import { photoUrl } from "../lib/photos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending_payment: ["Awaiting payment", "bg-amber-50 text-amber-700 border-amber-200"],
  paid: ["Finding a Mate", "bg-blue-50 text-blue-700 border-blue-200"],
  accepted: ["Mate assigned", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  in_progress: ["In progress", "bg-indigo-50 text-indigo-700 border-indigo-200"],
  completed: ["Completed", "bg-neutral-100 text-neutral-800 border-neutral-200"],
  cancelled: ["Cancelled", "bg-red-50 text-red-700 border-red-200"],
};

function StatusPill({ status }) {
  const [label, cls] = STATUS_MAP[status] || [status, ""];
  return <span className={`text-xs px-2.5 py-1 rounded-full border ${cls}`}>{label}</span>;
}

function BookingCard({ b, role, onRefresh }) {
  const { authFetch } = useAuth();
  const [rateOpen, setRateOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState("");

  const action = async (path) => {
    try {
      await authFetch({ method: "POST", url: `${API}${path}` });
      toast.success("Updated");
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitRating = async () => {
    try {
      await authFetch({
        method: "POST",
        url: `${API}/bookings/${b.id}/rate`,
        data: { rating, feedback },
      });
      toast.success("Thanks for rating!");
      setRateOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid={`booking-card-${b.id}`} className="rounded-3xl border border-black/10 bg-white p-6 card-lift">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">{b.service_name}</div>
          <div className="font-display text-2xl mt-1">₹{b.price_inr}</div>
        </div>
        <StatusPill status={b.status} />
      </div>
      <div className="mt-4 space-y-1.5 text-sm text-neutral-700">
        <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-neutral-500" /> {new Date(b.scheduled_at).toLocaleString()}</div>
        <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-neutral-500" /> {b.address}</div>
        {b.hours > 1 && <div className="text-neutral-500">Duration · {b.hours} hours</div>}
        {b.assistant_name && (
          <div className="flex items-center gap-2 text-[color:var(--secondary)]">
            <ShieldCheck className="w-3.5 h-3.5" /> Mate: {b.assistant_name}
          </div>
        )}
        {role === "assistant" && (
          <div className="text-neutral-500">Customer · {b.customer_name}{b.customer_phone ? ` · ${b.customer_phone}` : ""}</div>
        )}
        {b.notes && <div className="text-neutral-500 italic">"{b.notes}"</div>}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {role === "assistant" && b.status === "accepted" && (
          <button data-testid={`start-btn-${b.id}`} onClick={() => action(`/bookings/${b.id}/start`)} className="btn-primary text-sm">Start service</button>
        )}
        {role === "assistant" && b.status === "in_progress" && (
          <button data-testid={`complete-btn-${b.id}`} onClick={() => action(`/bookings/${b.id}/complete`)} className="btn-primary text-sm">Mark complete</button>
        )}
        {role === "customer" && b.status === "completed" && b.rating == null && (
          <button data-testid={`rate-btn-${b.id}`} onClick={() => setRateOpen(true)} className="btn-primary text-sm">Rate & feedback</button>
        )}
        {b.rating != null && (
          <div className="flex items-center gap-1 text-sm">
            {Array.from({ length: b.rating }).map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
            ))}
            {b.feedback && <span className="ml-2 text-neutral-500 italic">"{b.feedback}"</span>}
          </div>
        )}
      </div>

      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Rate your Mate</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center gap-2 mt-2">
            {[1,2,3,4,5].map(n => (
              <button
                key={n}
                data-testid={`rate-star-${n}`}
                onClick={() => setRating(n)}
                className="p-1"
              >
                <Star className={`w-8 h-8 ${n <= rating ? "fill-yellow-500 text-yellow-500" : "text-neutral-300"}`} />
              </button>
            ))}
          </div>
          <Textarea
            data-testid="rate-feedback-input"
            className="mt-4"
            placeholder="Share a note (optional)"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
          <button data-testid="rate-submit-btn" className="btn-primary mt-4 w-full" onClick={submitRating}>Submit</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Dashboard() {
  const { user, authFetch } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch({ method: "GET", url: `${API}/bookings/mine` });
      setBookings(r.data.bookings);
      if (user?.role === "assistant") {
        const a = await authFetch({ method: "GET", url: `${API}/bookings/available` });
        setAvailable(a.data.bookings);
      }
    } finally {
      setLoading(false);
    }
  }, [authFetch, user]);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-24 text-center">
        <HelpingHand className="w-10 h-10 text-[color:var(--primary)] mx-auto" />
        <h1 className="font-display text-4xl mt-4">Sign in to view your dashboard</h1>
        <Link to="/login" className="btn-primary inline-block mt-6">Sign in</Link>
      </div>
    );
  }

  const acceptJob = async (bid) => {
    try {
      await authFetch({ method: "POST", url: `${API}/bookings/${bid}/accept` });
      toast.success("Job accepted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10 pb-24">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="flex items-center gap-4">
          {user.photo_path ? (
            <img data-testid="dashboard-avatar" src={photoUrl(user.photo_path)} alt={user.name} className="w-16 h-16 rounded-full object-cover border-2 border-black/10" />
          ) : null}
          <div>
            <span className="label-eyebrow">welcome, {user.name.split(" ")[0]}</span>
            <h1 className="font-display text-4xl md:text-5xl font-bold mt-2">
              {user.role === "assistant" ? "Mate workspace" : "Your bookings"}
            </h1>
            <div className="mt-2 flex items-center gap-2">
              {user.is_member && <span className="chip"><Sparkles className="w-3.5 h-3.5" style={{ color: "var(--secondary)" }} /> Priority</span>}
              {user.is_featured_mate && <span className="chip" style={{ borderColor: "var(--secondary)", color: "var(--secondary)" }}><Crown className="w-3.5 h-3.5" /> Featured Mate</span>}
            </div>
          </div>
        </div>
        {user.is_member ? (
          <span className="chip"><Sparkles className="w-3.5 h-3.5" style={{ color: "var(--secondary)" }} /> Priority Member</span>
        ) : (
          <DashboardHeaderCTA user={user} />
        )}
      </div>

      {!user.photo_path && (
        <div data-testid="dashboard-photo-nudge" className="mt-6 rounded-3xl border border-dashed border-black/15 bg-white p-6">
          <div className="label-eyebrow">add your photo</div>
          <p className="text-sm text-neutral-600 mt-2 mb-4">A friendly photo builds trust — takes 3 seconds.</p>
          <DashboardPhotoUploader />
        </div>
      )}

      {user.role === "assistant" ? (
        <Tabs defaultValue="available" className="mt-10">
          <TabsList>
            <TabsTrigger value="available" data-testid="tab-available">Available jobs ({available.length})</TabsTrigger>
            <TabsTrigger value="mine" data-testid="tab-mine">My assignments ({bookings.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="available" className="mt-6">
            {available.length === 0 ? (
              <EmptyState text="No paid Assists waiting right now. Check back soon." />
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {available.map((b) => (
                  <div key={b.id} className="rounded-3xl border border-black/10 bg-white p-6 card-lift">
                    <div className="text-xs uppercase tracking-widest text-neutral-500">{b.service_name}</div>
                    <div className="font-display text-2xl mt-1">₹{b.partner_earning_inr} <span className="text-xs text-neutral-500 font-sans">to you</span></div>
                    <div className="mt-3 text-sm text-neutral-700 space-y-1.5">
                      <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-neutral-500" /> {new Date(b.scheduled_at).toLocaleString()}</div>
                      <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-neutral-500" /> {b.address}</div>
                      {b.notes && <div className="italic text-neutral-500">"{b.notes}"</div>}
                    </div>
                    <button
                      data-testid={`accept-btn-${b.id}`}
                      onClick={() => acceptJob(b.id)}
                      className="btn-primary w-full mt-4"
                    >
                      Accept job
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="mine" className="mt-6">
            {bookings.length === 0 ? (
              <EmptyState text="No assignments yet." />
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bookings.map((b) => <BookingCard key={b.id} b={b} role="assistant" onRefresh={refresh} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      ) : (
        <div className="mt-10">
          <CustomerBookingsList loading={loading} bookings={bookings} onRefresh={refresh} />
        </div>
      )}
    </div>
  );
}

function DashboardPhotoUploader() {
  const { token, user, authFetch } = useAuth();
  const [uploaded, setUploaded] = useState(user?.photo_path || null);
  const onUpload = async (path) => {
    setUploaded(path);
    // Refresh user from server so is_featured_mate/photo_path propagate
    try {
      const r = await authFetch({ method: "GET", url: `${API}/auth/me` });
      // Simplest: reload the page so AuthContext re-picks the user
      if (r.data?.photo_path) window.location.reload();
    } catch { /* ignore */ }
  };
  return <PhotoUpload value={uploaded} token={token} onUpload={onUpload} />;
}

function DashboardHeaderCTA({ user }) {
  if (user.is_member) {
    return (
      <span className="chip"><Sparkles className="w-3.5 h-3.5" style={{ color: "var(--secondary)" }} /> Priority Member</span>
    );
  }
  if (user.role !== "customer") return null;
  return (
    <Link to="/membership" data-testid="upgrade-membership-link">
      <button className="px-5 py-2 rounded-full text-white text-sm font-bold hover:opacity-90" style={{ background: "var(--secondary)" }}>
        Upgrade to Priority ₹99
      </button>
    </Link>
  );
}

function CustomerBookingsList({ loading, bookings, onRefresh }) {
  if (loading) return <div className="text-neutral-500">Loading…</div>;
  if (bookings.length === 0) {
    return <EmptyState text="No bookings yet — book a real Mate in seconds." cta />;
  }
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {bookings.map((b) => <BookingCard key={b.id} b={b} role="customer" onRefresh={onRefresh} />)}
    </div>
  );
}

function EmptyState({ text, cta = false }) {
  return (
    <div className="rounded-3xl border border-dashed border-black/15 bg-white p-16 text-center">
      <HelpingHand className="w-8 h-8 text-neutral-400 mx-auto" />
      <p className="mt-3 text-neutral-600">{text}</p>
      {cta && (
        <Link to="/services" className="btn-primary inline-block mt-5">Book an Assist</Link>
      )}
    </div>
  );
}
